import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createExit, apiRequest, createProduct } from "@/lib/api";
import { getCurrentUser, getCurrentShop } from "@/lib/auth";
import { PRODUCT_CATEGORIES } from "@shared/categories";
import { Loader2, Plus, Trash2, Search, Pencil } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const EXIT_TYPES = {
  exit: { label: "Salida", description: "Salida de inventario" },
  waste: { label: "Merma", description: "Pérdida o deterioro" },
};

const WASTE_REASONS = [
  "Rotura durante manipulación",
  "Producto caducado",
  "Deterioro",
  "Derrame",
  "Error de inventario",
  "Donación",
  "Otro",
];

interface ExitLine {
  inventoryId: string;
  productId: string;
  productName: string;
  quantity: number;
  unitCost: string;
  batchNumber: string;
}

export function ExitModal({
  show,
  onClose,
  onSuccess,
}: {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const currentShop = getCurrentShop()!;
  const currentUser = getCurrentUser();

  const [exitType, setExitType] = useState<"exit" | "waste">("waste");
  const [wasteReason, setWasteReason] = useState("");
  const [notes, setNotes] = useState("");

  // Product search
  const [productSearch, setProductSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);

  // Lines
  const [lines, setLines] = useState<ExitLine[]>([]);
  const [editingProduct, setEditingProduct] = useState<ExitLine | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);

  // Inline create product
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [newProductCategory, setNewProductCategory] = useState("");
  const [newProductPrice, setNewProductPrice] = useState("");
  const [newProductCost, setNewProductCost] = useState("");
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);

  const resetForm = () => {
    setExitType("waste");
    setWasteReason("");
    setNotes("");
    setProductSearch("");
    setSearchResults([]);
    setShowSearch(false);
    setLines([]);
    setEditingProduct(null);
    setShowAddProduct(false);
    setShowCreateProduct(false);
    setNewProductCategory("");
    setNewProductPrice("");
    setNewProductCost("");
    setIsCreatingProduct(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const searchProducts = async (q: string) => {
    setProductSearch(q);
    setShowCreateProduct(false);
    if (q.length < 1) { setSearchResults([]); return; }
    try {
      const data = await apiRequest<any>(
        `/api/shops/${currentShop.id}/inventory?search=${encodeURIComponent(q)}&limit=10`,
      );
      setSearchResults(Array.isArray(data) ? data : data?.data || []);
    } catch { setSearchResults([]); }
  };

  const selectProduct = (item: any) => {
    const exists = lines.find((l) => l.inventoryId === item.id);
    if (exists) {
      toast({ title: "Producto ya agregado", variant: "destructive" });
      return;
    }
    setEditingProduct({
      inventoryId: item.id,
      productId: item.productId,
      productName: item.product?.name || item.productName || "Producto",
      quantity: 1,
      unitCost: item.averageCost || item.acquisitionCost || "0",
      batchNumber: item.batchNumber || "",
    });
    setShowAddProduct(true);
    setShowSearch(false);
    setProductSearch("");
    setSearchResults([]);
  };

  const confirmAddProduct = () => {
    if (!editingProduct) return;
    if (editingProduct.quantity <= 0) {
      toast({ title: "La cantidad debe ser mayor a 0", variant: "destructive" });
      return;
    }
    if (!editingProduct.unitCost || parseFloat(editingProduct.unitCost) <= 0) {
      toast({ title: "El costo unitario es requerido", variant: "destructive" });
      return;
    }
    setLines((prev) => {
      const existing = prev.findIndex((l) => l.inventoryId === editingProduct.inventoryId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...editingProduct };
        return updated;
      }
      return [...prev, { ...editingProduct }];
    });
    setEditingProduct(null);
    setShowAddProduct(false);
  };

  const removeLine = (inventoryId: string) => {
    setLines((prev) => prev.filter((l) => l.inventoryId !== inventoryId));
  };

  const startEditLine = (inventoryId: string) => {
    const line = lines.find((l) => l.inventoryId === inventoryId);
    if (!line) return;
    setEditingProduct({ ...line });
    setShowAddProduct(true);
    setShowSearch(false);
  };

  const handleCreateAndAdd = async () => {
    const name = productSearch.trim();
    const category = newProductCategory;
    const cost = newProductCost || "0";
    if (!name || !category) {
      toast({ title: "Debe seleccionar una categoría", variant: "destructive" });
      return;
    }
    setIsCreatingProduct(true);
    try {
      const price = newProductPrice || "0";
      const result = await createProduct(currentShop.id, {
        name,
        category,
        price,
        stock: 0,
        threshold: 10,
        acquisitionCost: cost,
      });
      const newItem = result.inventory;
      setEditingProduct({
        inventoryId: newItem.id,
        productId: newItem.productId,
        productName: name,
        quantity: 1,
        unitCost: cost,
        batchNumber: "",
      });
      setShowCreateProduct(false);
      setShowAddProduct(true);
      setSearchResults([]);
      setProductSearch("");
      setNewProductCategory("");
      setNewProductPrice("");
      setNewProductCost("");
      toast({ title: `Producto "${name}" creado y listo para agregar` });
    } catch (error: any) {
      toast({
        title: "Error al crear producto",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const handleSave = async () => {
    if (lines.length === 0) {
      toast({ title: "Debe agregar al menos un producto", variant: "destructive" });
      return;
    }
    if (exitType === "waste" && !wasteReason) {
      toast({ title: "Debe seleccionar un motivo", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      await createExit(currentShop.id, {
        type: exitType,
        referenceType: exitType === "waste" ? wasteReason : "salida",
        notes: notes || undefined,
        registeredById: currentUser?.id,
        lines: lines.map((l) => ({
          inventoryId: l.inventoryId,
          productId: l.productId,
          quantity: l.quantity,
          unitCost: l.unitCost || undefined,
          batchNumber: l.batchNumber || undefined,
        })),
      });

      toast({ title: exitType === "waste" ? "Merma registrada" : "Salida registrada" });
      resetForm();
      onSuccess();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const totalQuantity = lines.reduce((s, l) => s + l.quantity, 0);
  const totalCost = lines.reduce((s, l) => s + l.quantity * parseFloat(l.unitCost || "0"), 0);

  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {exitType === "waste" ? "📤 Registrar Merma" : "📤 Registrar Salida"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Header: type + reason */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Tipo</Label>
              <Select value={exitType} onValueChange={(v) => setExitType(v as "exit" | "waste")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(EXIT_TYPES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {exitType === "waste" && (
              <div>
                <Label>Motivo *</Label>
                <Select value={wasteReason} onValueChange={setWasteReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar motivo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {WASTE_REASONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Product table */}
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Producto</th>
                  <th className="p-2 text-right">Cant</th>
                  <th className="p-2 text-right">Costo Und</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2 text-center">Lote</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => (
                  <tr key={line.inventoryId} className="border-b">
                    <td className="p-2">{i + 1}</td>
                    <td className="p-2 font-medium">{line.productName}</td>
                    <td className="p-2 text-right">{line.quantity}</td>
                    <td className="p-2 text-right">${parseFloat(line.unitCost).toFixed(2)}</td>
                    <td className="p-2 text-right">${(line.quantity * parseFloat(line.unitCost)).toFixed(2)}</td>
                    <td className="p-2 text-center text-xs text-muted-foreground">{line.batchNumber || "—"}</td>
                    <td className="p-2">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => startEditLine(line.inventoryId)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => removeLine(line.inventoryId)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-muted-foreground">
                      No hay productos agregados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Add product search (only when not creating or editing inline) */}
          {!showAddProduct && !showCreateProduct && (
            <div className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={productSearch}
                    onChange={(e) => searchProducts(e.target.value)}
                    onFocus={() => setShowSearch(true)}
                    className="pl-9"
                    placeholder="Buscar producto para agregar..."
                  />
                </div>
                <Button onClick={() => setShowSearch(!showSearch)} variant="outline">
                  {showSearch ? "Cerrar" : "Buscar"}
                </Button>
              </div>
              {showSearch && productSearch.length > 0 && (
                <div className="absolute z-10 w-full mt-1 border rounded-lg bg-background shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.length === 0 ? (
                    <div className="p-3">
                      <p className="text-sm text-muted-foreground mb-2">
                        No se encontró "<strong>{productSearch}</strong>" en el catálogo
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setShowCreateProduct(true);
                          setNewProductCategory("");
                          setNewProductPrice("");
                          setNewProductCost("");
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Crear nuevo producto "{productSearch}"
                      </Button>
                    </div>
                  ) : (
                    searchResults.slice(0, 10).map((item: any) => (
                      <button
                        key={item.id}
                        onClick={() => selectProduct(item)}
                        className="w-full p-3 text-left hover:bg-muted flex justify-between items-center border-b last:border-0"
                      >
                        <span className="font-medium">
                          {item.product?.name || item.productName || "Producto"}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          Stock: {item.stock} | Costo: ${parseFloat(item.averageCost || item.acquisitionCost || "0").toFixed(2)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Create new product inline form */}
          {showCreateProduct && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <h4 className="font-medium flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Nuevo producto: {productSearch}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Categoría *</Label>
                  <Select value={newProductCategory} onValueChange={setNewProductCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar categoría..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {PRODUCT_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Costo adquisición (CUP) *</Label>
                  <Input
                    value={newProductCost}
                    onChange={(e) => setNewProductCost(e.target.value)}
                    placeholder="Costo de compra"
                  />
                </div>
                <div>
                  <Label>Precio venta (CUP)</Label>
                  <Input
                    value={newProductPrice}
                    onChange={(e) => setNewProductPrice(e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => { setShowCreateProduct(false); setShowSearch(true); }}>
                  ← Volver
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreateAndAdd}
                  disabled={isCreatingProduct || !newProductCategory}
                >
                  {isCreatingProduct ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creando...</>
                  ) : (
                    "✓ Crear y agregar"
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Inline product edit form */}
          {showAddProduct && editingProduct && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <h4 className="font-medium">{editingProduct.productName}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Cantidad *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editingProduct.quantity}
                    onChange={(e) =>
                      setEditingProduct((prev) =>
                        prev ? { ...prev, quantity: Number(e.target.value) } : null,
                      )
                    }
                  />
                </div>
                <div>
                  <Label>Costo unitario (CUP) *</Label>
                  <Input
                    value={editingProduct.unitCost}
                    onChange={(e) =>
                      setEditingProduct((prev) =>
                        prev ? { ...prev, unitCost: e.target.value } : null,
                      )
                    }
                  />
                </div>
                <div>
                  <Label>Número de lote</Label>
                  <Input
                    value={editingProduct.batchNumber}
                    onChange={(e) =>
                      setEditingProduct((prev) =>
                        prev ? { ...prev, batchNumber: e.target.value } : null,
                      )
                    }
                    placeholder="LOTE-2026-XXX"
                  />
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setShowAddProduct(false); setEditingProduct(null); }}>
                  Cancelar
                </Button>
                <Button onClick={confirmAddProduct}>
                  ✓ {editingProduct && lines.some((l) => l.inventoryId === editingProduct.inventoryId) ? "Actualizar" : "Agregar"}
                </Button>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label>Notas</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas adicionales..."
            />
          </div>

          {/* Waste loss summary */}
          {exitType === "waste" && lines.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-sm text-red-700 font-medium">
                Pérdida total estimada: ${totalCost.toFixed(2)}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-2 pt-4 border-t">
          <div>
            {lines.length > 0 && (
              <div className="text-sm text-muted-foreground">
                {lines.length} producto(s) | {totalQuantity} unidades | ${totalCost.toFixed(2)}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>Cancelar</Button>
            <Button onClick={handleSave} disabled={isSaving || lines.length === 0}>
              {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : "✅ Registrar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
